import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from "@material-ui/core/styles";
import AdminLayout from '../../layouts/AdminLayout';
import { 
  getHadithCategories, addHadithCategory, updateHadithCategoryOrder, updateHadithCategory, deleteHadithCategory,
  getHadithBooks, addHadithBook, updateHadithBookOrder, updateHadithBook, deleteHadithBook
} from '../../services/hadithService';
import { getDuaCategories, addDuaCategory, updateDuaCategoryOrder, updateDuaCategory, deleteDuaCategory } from '../../services/duaService';
import { getAllDuaSubcategories, createDuaSubcategory, updateDuaSubcategory, deleteDuaSubcategory } from '../../services/duaSubcategoryService';
import {
  Paper, Typography, Grid, List, ListItem, ListItemText, ListItemSecondaryAction,
  IconButton, makeStyles, CircularProgress, Snackbar, Divider, Tooltip, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Tabs, Tab, Box, MenuItem
} from '@material-ui/core';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import FavoriteIcon from '@material-ui/icons/Favorite';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

const useStyles = makeStyles((theme) => ({
  tabsPaper: {
    padding: theme.spacing(2, 4, 0, 4),
    borderRadius: 16,
    background: theme.palette.type === "dark" ? "rgba(30,41,59,0.7)" : "rgba(255,255,255,0.7)",
    backdropFilter: 'blur(20px)',
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
    marginBottom: theme.spacing(3)
  },
  paper: {
    padding: theme.spacing(4),
    borderRadius: 16,
    background: theme.palette.type === "dark" ? "rgba(30,41,59,0.7)" : "rgba(255,255,255,0.7)",
    backdropFilter: 'blur(20px)',
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
    minHeight: 500 
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    color: theme.palette.text.primary 
  },
  listContainer: {
    background: theme.palette.type === "dark" ? "rgba(15,23,42,0.4)" : "rgba(255,255,255,0.4)",
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    overflowY: 'auto',
    maxHeight: 'calc(100vh - 280px)',
  },
  listItem: {
    borderBottom: '1px solid rgba(148,163,184,0.08)',
    cursor: 'pointer',
    '&:last-child': {
      borderBottom: 'none' 
    },
    '&:hover': {
      backgroundColor: 'rgba(148,163,184,0.05)' 
    } 
  },
  selectedListItem: {
    backgroundColor: 'rgba(236,72,153,0.1)',
    borderLeft: '4px solid #ec4899',
    '&:hover': {
      backgroundColor: 'rgba(236,72,153,0.15)' 
    } 
  },
  actionBtn: {
    color: theme.palette.text.secondary,
    padding: 8,
    '&:hover': {
      backgroundColor: 'rgba(16,185,129,0.1)',
      color: '#10b981' 
    } 
  },
  orderBadge: {
    background: theme.palette.action.hover,
    color: theme.palette.text.secondary,
    minWidth: 28,
    height: 28,
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
    fontSize: '0.8rem',
    fontWeight: 600 
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4) 
  }
}));

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`category-tabpanel-${index}`}
      aria-labelledby={`category-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box>
          {children}
        </Box>
      )}
    </div>
  );
}

const INITIAL_HADITH_BOOKS = [
  { id: 'bukhari', name: 'Sahih Al-Bukhari' },
  { id: 'muslim', name: 'Sahih Muslim' },
  { id: 'abudawud', name: 'Sunan Abu Dawud' },
  { id: 'tirmidhi', name: "Jami' at-Tirmidhi" },
  { id: 'nasai', name: 'Sunan An-Nasai' },
  { id: 'ibnmajah', name: 'Sunan Ibn Majah' },
];

const INITIAL_DUA_CATEGORIES = [
  'morning', 'evening', 'night', 'daily', 'travel', 'eating',
  'sleeping', 'prayer', 'protection', 'gratitude', 'other', 'special', 'ramadan'
].map(c => ({ id: c, name: c.charAt(0).toUpperCase() + c.slice(1) }));

const INITIAL_HADITH_CATEGORIES = [
  { id: 'iman', name: 'Iman' },
  { id: 'prayer', name: 'Prayer' },
  { id: 'uju', name: 'Uju' },
];

export default function CategoryManagerPage() {
  const classes = useStyles();
  const theme = useTheme();
  
  const [loading, setLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState(0);
  
  const [hadithCats, setHadithCats] = useState([]);
  const [hadithBooks, setHadithBooks] = useState([]);
  const [duaCats, setDuaCats] = useState([]);
  const [duaSubcats, setDuaSubcats] = useState([]);
  const [selectedDuaCatId, setSelectedDuaCatId] = useState(null);

  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  // Dialog State
  const [editDialog, setEditDialog] = useState({ open: false, type: '', item: null, nameEn: '', nameBn: '', categoryId: '' });
  const [addDialog, setAddDialog] = useState({ open: false, type: '', id: '', nameEn: '', nameBn: '', categoryId: '' });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: '', item: null });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      let hCats = await getHadithCategories();
      let hBooks = await getHadithBooks();
      let dCats = await getDuaCategories();
      let dSubcats = await getAllDuaSubcategories();

      // Sync initial Hadith categories to Firestore if missing
      for (const c of INITIAL_HADITH_CATEGORIES) {
        if (!hCats.find(existing => existing.id === c.id)) {
          await addHadithCategory(c.id, c.name, c.name);
          hCats = await getHadithCategories();
        }
      }

      // Sync initial Hadith books to Firestore if missing
      for (const b of INITIAL_HADITH_BOOKS) {
        if (!hBooks.find(existing => existing.id === b.id)) {
          await addHadithBook(b.id, b.name, b.name);
          hBooks = await getHadithBooks();
        }
      }

      // Sync initial Dua categories to Firestore if missing
      for (const c of INITIAL_DUA_CATEGORIES) {
        if (!dCats.find(d => d.id === c.id)) {
          await addDuaCategory(c.id, c.name);
          dCats = await getDuaCategories();
        }
      }

      setHadithCats(hCats.sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0)));
      setHadithBooks(hBooks.sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0)));
      setDuaCats(dCats.sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0)));
      setDuaSubcats(dSubcats.filter(s => !s.isDeleted).sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0)));
      
      if (dCats.length > 0) {
        setSelectedDuaCatId(prev => prev || dCats[0].id);
      }
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Failed to load categories' });
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleReorder = async (type, index, direction) => {
    // Only support reordering for Categories and Books (not subcats right now)
    let list;
    let updateFn;
    
    if (type === 'hadithCat') {
      list = [...hadithCats];
      updateFn = updateHadithCategoryOrder;
    } else if (type === 'hadithBook') {
      list = [...hadithBooks];
      updateFn = updateHadithBookOrder;
    } else if (type === 'duaCat') {
      list = [...duaCats];
      updateFn = updateDuaCategoryOrder;
    } else {
      return;
    }
    
    const targetIdx = direction === 'up' ? index - 1 : index + 1;
    if (targetIdx < 0 || targetIdx >= list.length) return;

    const item = list[index];
    const target = list[targetIdx];
    
    const orderA = item.orderIndex ?? index;
    const orderB = target.orderIndex ?? targetIdx;
    
    const newOrderForTarget = orderA;
    const newOrderForItem = orderB;
    
    list[index] = { ...item, orderIndex: newOrderForItem };
    list[targetIdx] = { ...target, orderIndex: newOrderForTarget };
    
    list.sort((a, b) => a.orderIndex - b.orderIndex);
    
    if (type === 'hadithCat') setHadithCats(list);
    else if (type === 'hadithBook') setHadithBooks(list);
    else setDuaCats(list);

    try {
      await Promise.all([
        updateFn(item.id, newOrderForItem),
        updateFn(target.id, newOrderForTarget)
      ]);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to reorder' });
      loadData();
    }
  };

  const openEdit = (type, item) => {
    setEditDialog({ 
      open: true, 
      type, 
      item, 
      nameEn: item.nameEn || item.titleEn || item.name || '',
      nameBn: item.nameBn || item.titleBn || item.name || '',
      categoryId: item.categoryId || ''
    });
  };

  const closeEdit = () => {
    setEditDialog({ open: false, type: '', item: null, nameEn: '', nameBn: '', categoryId: '' });
  };

  const saveEdit = async () => {
    const { type, item, nameEn, nameBn, categoryId } = editDialog;
    if (!nameEn.trim()) return;

    try {
      if (type === 'hadithCat') {
        await updateHadithCategory(item.id, { nameEn, nameBn, name: nameEn });
      } else if (type === 'hadithBook') {
        await updateHadithBook(item.id, { nameEn, nameBn, name: nameEn });
      } else if (type === 'duaCat') {
        await updateDuaCategory(item.id, { nameEn, nameBn, name: nameEn });
      } else if (type === 'duaSubcat') {
        await updateDuaSubcategory(item.id, { titleEn: nameEn, titleBn: nameBn, categoryId });
      }
      setSnackbar({ open: true, message: 'Updated successfully' });
      closeEdit();
      loadData();
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Failed to update' });
    }
  };

  const openAdd = (type) => {
    setAddDialog({ 
      open: true, 
      type, 
      id: '', 
      nameEn: '', 
      nameBn: '', 
      categoryId: type === 'duaSubcat' ? selectedDuaCatId : '' 
    });
  };

  const closeAdd = () => {
    setAddDialog({ open: false, type: '', id: '', nameEn: '', nameBn: '', categoryId: '' });
  };

  const saveAdd = async () => {
    let { type, id, nameEn, nameBn, categoryId } = addDialog;
    if (!nameEn.trim()) return;
    if (type !== 'duaSubcat' && !id.trim()) return; // subcat auto-generates id
    
    if (id) id = id.trim().toLowerCase().replace(/\s+/g, '_');

    try {
      if (type === 'hadithCat') {
        await addHadithCategory(id, nameEn, nameBn);
      } else if (type === 'hadithBook') {
        await addHadithBook(id, nameEn, nameBn);
      } else if (type === 'duaCat') {
        await addDuaCategory(id, nameEn); // nameEn handles name internally in dua service
        if (nameBn) await updateDuaCategory(id, { nameBn });
      } else if (type === 'duaSubcat') {
        await createDuaSubcategory({ titleEn: nameEn, titleBn: nameBn, categoryId });
      }
      setSnackbar({ open: true, message: 'Added successfully' });
      closeAdd();
      loadData();
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Failed to add' });
    }
  };

  const openDelete = (type, item) => {
    setDeleteDialog({ open: true, type, item });
  };

  const closeDelete = () => {
    setDeleteDialog({ open: false, type: '', item: null });
  };

  const confirmDelete = async () => {
    const { type, item } = deleteDialog;
    try {
      if (type === 'hadithCat') {
        await deleteHadithCategory(item.id);
      } else if (type === 'hadithBook') {
        await deleteHadithBook(item.id);
      } else if (type === 'duaCat') {
        // Safe check for subcats
        if (duaSubcats.some(s => s.categoryId === item.id)) {
           throw new Error('Cannot delete: This category has subcategories.');
        }
        await deleteDuaCategory(item.id);
      } else if (type === 'duaSubcat') {
        await deleteDuaSubcategory(item.id);
      }
      setSnackbar({ open: true, message: 'Deleted successfully' });
      closeDelete();
      loadData();
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: err.message || 'Failed to delete' });
    }
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const selectedSubcats = duaSubcats.filter(s => s.categoryId === selectedDuaCatId);

  return (
    <AdminLayout title="Category Manager">
      {loading ? (
        <div className={classes.loadingContainer}><CircularProgress /></div>
      ) : (
        <>
          <Paper className={classes.tabsPaper} elevation={0}>
            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              centered
            >
              <Tab label="Hadith Categories" />
              <Tab label="Hadith Books" />
              <Tab label="Dua Categories" />
            </Tabs>
          </Paper>

          <TabPanel value={currentTab} index={0}>
            <Paper className={classes.paper} elevation={0}>
              <div className={classes.header}>
                <MenuBookIcon style={{ color: '#10b981' }} />
                <Typography variant="h6" style={{ fontWeight: 600 }}>Hadith Categories</Typography>
                <Chip size="small" label={`${hadithCats.length} Items`} style={{ background: 'rgba(16,185,129,0.1)', color: '#10b981', marginLeft: 'auto' }} />
                <Button variant="outlined" color="primary" size="small" startIcon={<AddIcon />} onClick={() => openAdd('hadithCat')} style={{ marginLeft: 8 }}>
                  Add
                </Button>
              </div>
              <Divider style={{ backgroundColor: theme.palette.action.hover, marginBottom: 16 }} />
              <List className={classes.listContainer} disablePadding>
                {hadithCats.map((cat, idx) => (
                  <ListItem key={cat.id} className={classes.listItem}>
                    <span className={classes.orderBadge}>{idx + 1}</span>
                    <ListItemText 
                      primary={`${cat.nameEn || cat.name || ''} ${cat.nameBn ? `(${cat.nameBn})` : ''}`} 
                      secondary={`ID: ${cat.id}`}
                      primaryTypographyProps={{ style: { color: theme.palette.text.primary, fontWeight: 500 } }}
                      secondaryTypographyProps={{ style: { color: theme.palette.text.secondary, fontSize: '0.75rem' } }}
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title="Edit">
                        <span>
                          <IconButton className={classes.actionBtn} onClick={() => openEdit('hadithCat', cat)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Move Up">
                        <span>
                          <IconButton className={classes.actionBtn} onClick={() => handleReorder('hadithCat', idx, 'up')} disabled={idx === 0}>
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Move Down">
                        <span>
                          <IconButton className={classes.actionBtn} onClick={() => handleReorder('hadithCat', idx, 'down')} disabled={idx === hadithCats.length - 1}>
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <span>
                          <IconButton className={classes.actionBtn} onClick={() => openDelete('hadithCat', cat)} style={{ color: theme.palette.error.main }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Paper>
          </TabPanel>

          <TabPanel value={currentTab} index={1}>
            <Paper className={classes.paper} elevation={0}>
              <div className={classes.header}>
                <MenuBookIcon style={{ color: '#10b981' }} />
                <Typography variant="h6" style={{ fontWeight: 600 }}>Hadith Books</Typography>
                <Chip size="small" label={`${hadithBooks.length} Books`} style={{ background: 'rgba(16,185,129,0.1)', color: '#10b981', marginLeft: 'auto' }} />
                <Button variant="outlined" color="primary" size="small" startIcon={<AddIcon />} onClick={() => openAdd('hadithBook')} style={{ marginLeft: 8 }}>
                  Add
                </Button>
              </div>
              <Divider style={{ backgroundColor: theme.palette.action.hover, marginBottom: 16 }} />
              <List className={classes.listContainer} disablePadding>
                {hadithBooks.map((book, idx) => (
                  <ListItem key={book.id} className={classes.listItem}>
                    <span className={classes.orderBadge}>{idx + 1}</span>
                    <ListItemText
                      primary={`${book.nameEn || book.name || ''} ${book.nameBn ? `(${book.nameBn})` : ''}`}
                      secondary={`ID: ${book.id}`}
                      primaryTypographyProps={{ style: { color: theme.palette.text.primary, fontWeight: 500 } }}
                      secondaryTypographyProps={{ style: { color: theme.palette.text.secondary, fontSize: '0.75rem' } }}
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title="Edit">
                        <span>
                          <IconButton className={classes.actionBtn} onClick={() => openEdit('hadithBook', book)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Move Up">
                        <span>
                          <IconButton className={classes.actionBtn} onClick={() => handleReorder('hadithBook', idx, 'up')} disabled={idx === 0}>
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Move Down">
                        <span>
                          <IconButton className={classes.actionBtn} onClick={() => handleReorder('hadithBook', idx, 'down')} disabled={idx === hadithBooks.length - 1}>
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <span>
                          <IconButton className={classes.actionBtn} onClick={() => openDelete('hadithBook', book)} style={{ color: theme.palette.error.main }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Paper>
          </TabPanel>

          <TabPanel value={currentTab} index={2}>
            <Grid container spacing={3}>
              {/* Left Pane - Categories */}
              <Grid item xs={12} md={6}>
                <Paper className={classes.paper} elevation={0}>
                  <div className={classes.header}>
                    <FavoriteIcon style={{ color: '#ec4899' }} />
                    <Typography variant="h6" style={{ fontWeight: 600 }}>Dua Categories</Typography>
                    <Chip size="small" label={`${duaCats.length} Categories`} style={{ background: 'rgba(236,72,153,0.1)', color: '#ec4899', marginLeft: 'auto' }} />
                    <Button variant="outlined" color="primary" size="small" startIcon={<AddIcon />} onClick={() => openAdd('duaCat')} style={{ marginLeft: 8 }}>
                      Add
                    </Button>
                  </div>
                  <Divider style={{ backgroundColor: theme.palette.action.hover, marginBottom: 16 }} />
                  <List className={classes.listContainer} disablePadding>
                    {duaCats.map((cat, idx) => (
                      <ListItem 
                        key={cat.id} 
                        className={`${classes.listItem} ${selectedDuaCatId === cat.id ? classes.selectedListItem : ''}`}
                        onClick={() => setSelectedDuaCatId(cat.id)}
                      >
                        <span className={classes.orderBadge}>{idx + 1}</span>
                        <ListItemText 
                          primary={`${cat.nameEn || cat.name || ''} ${cat.nameBn ? `(${cat.nameBn})` : ''}`} 
                          secondary={`ID: ${cat.id}`}
                          primaryTypographyProps={{ style: { color: theme.palette.text.primary, fontWeight: 500 } }}
                          secondaryTypographyProps={{ style: { color: theme.palette.text.secondary, fontSize: '0.75rem' } }}
                        />
                        <ListItemSecondaryAction>
                          <Tooltip title="Edit">
                            <span>
                              <IconButton className={classes.actionBtn} onClick={(e) => { e.stopPropagation(); openEdit('duaCat', cat); }}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Move Up">
                            <span>
                              <IconButton className={classes.actionBtn} onClick={(e) => { e.stopPropagation(); handleReorder('duaCat', idx, 'up'); }} disabled={idx === 0}>
                                <ArrowUpwardIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Move Down">
                            <span>
                              <IconButton className={classes.actionBtn} onClick={(e) => { e.stopPropagation(); handleReorder('duaCat', idx, 'down'); }} disabled={idx === duaCats.length - 1}>
                                <ArrowDownwardIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <span>
                              <IconButton className={classes.actionBtn} onClick={(e) => { e.stopPropagation(); openDelete('duaCat', cat); }} style={{ color: theme.palette.error.main }}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <ChevronRightIcon style={{ color: selectedDuaCatId === cat.id ? '#ec4899' : 'transparent', marginLeft: 8, verticalAlign: 'middle' }} />
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>

              {/* Right Pane - Subcategories */}
              <Grid item xs={12} md={6}>
                <Paper className={classes.paper} elevation={0}>
                  <div className={classes.header}>
                    <Typography variant="h6" style={{ fontWeight: 600 }}>
                      Subcategories
                    </Typography>
                    {selectedDuaCatId && (
                      <Typography variant="caption" style={{ color: theme.palette.text.secondary, marginLeft: 8 }}>
                        ({duaCats.find(c => c.id === selectedDuaCatId)?.nameEn || selectedDuaCatId})
                      </Typography>
                    )}
                    <Chip size="small" label={`${selectedSubcats.length} Subcats`} style={{ background: 'rgba(59,130,246,0.1)', color: '#3b82f6', marginLeft: 'auto' }} />
                    <Button 
                      variant="outlined" 
                      color="primary" 
                      size="small" 
                      startIcon={<AddIcon />} 
                      onClick={() => openAdd('duaSubcat')} 
                      style={{ marginLeft: 8 }}
                      disabled={!selectedDuaCatId}
                    >
                      Add
                    </Button>
                  </div>
                  <Divider style={{ backgroundColor: theme.palette.action.hover, marginBottom: 16 }} />
                  
                  {!selectedDuaCatId ? (
                    <Typography align="center" color="textSecondary" style={{ marginTop: 40 }}>
                      Select a category to view subcategories
                    </Typography>
                  ) : selectedSubcats.length === 0 ? (
                    <Typography align="center" color="textSecondary" style={{ marginTop: 40 }}>
                      No subcategories found for this category
                    </Typography>
                  ) : (
                    <List className={classes.listContainer} disablePadding>
                      {selectedSubcats.map((sub, idx) => (
                        <ListItem key={sub.id} className={classes.listItem}>
                          <span className={classes.orderBadge} style={{ background: 'rgba(59,130,246,0.1)', color: '#3b82f6' }}>{idx + 1}</span>
                          <ListItemText 
                            primary={`${sub.titleEn || ''} ${sub.titleBn ? `(${sub.titleBn})` : ''}`} 
                            secondary={`ID: ${sub.id}`}
                            primaryTypographyProps={{ style: { color: theme.palette.text.primary, fontWeight: 500 } }}
                            secondaryTypographyProps={{ style: { color: theme.palette.text.secondary, fontSize: '0.75rem' } }}
                          />
                          <ListItemSecondaryAction>
                            <Tooltip title="Edit">
                              <span>
                                <IconButton className={classes.actionBtn} onClick={() => openEdit('duaSubcat', sub)}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <span>
                                <IconButton className={classes.actionBtn} onClick={() => openDelete('duaSubcat', sub)} style={{ color: theme.palette.error.main }}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </TabPanel>
        </>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onClose={closeEdit} fullWidth maxWidth="xs">
        <DialogTitle>
          {editDialog.type === 'duaSubcat' ? 'Edit Subcategory' : 'Edit Category/Book'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="English Name"
            type="text"
            fullWidth
            variant="outlined"
            value={editDialog.nameEn}
            onChange={(e) => setEditDialog({ ...editDialog, nameEn: e.target.value })}
            style={{ marginBottom: 16 }}
          />
          <TextField
            margin="dense"
            label="Bangla Name"
            type="text"
            fullWidth
            variant="outlined"
            value={editDialog.nameBn}
            onChange={(e) => setEditDialog({ ...editDialog, nameBn: e.target.value })}
            style={{ marginBottom: 16 }}
          />
          {editDialog.type === 'duaSubcat' && (
            <TextField
              select
              margin="dense"
              label="Parent Category"
              fullWidth
              variant="outlined"
              value={editDialog.categoryId}
              onChange={(e) => setEditDialog({ ...editDialog, categoryId: e.target.value })}
            >
              {duaCats.map(cat => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.nameEn || cat.name}
                </MenuItem>
              ))}
            </TextField>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEdit} color="primary">
            Cancel
          </Button>
          <Button onClick={saveEdit} color="primary" variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Dialog */}
      <Dialog open={addDialog.open} onClose={closeAdd} fullWidth maxWidth="xs">
        <DialogTitle>
          {addDialog.type === 'duaSubcat' ? 'Add New Subcategory' : 'Add New Category/Book'}
        </DialogTitle>
        <DialogContent>
          {addDialog.type !== 'duaSubcat' && (
            <TextField
              autoFocus
              margin="dense"
              label="ID / Slug (e.g. daily_dua)"
              type="text"
              fullWidth
              variant="outlined"
              value={addDialog.id}
              onChange={(e) => setAddDialog({ ...addDialog, id: e.target.value })}
              style={{ marginBottom: 16 }}
            />
          )}
          <TextField
            margin="dense"
            label="English Name"
            type="text"
            fullWidth
            variant="outlined"
            value={addDialog.nameEn}
            onChange={(e) => setAddDialog({ ...addDialog, nameEn: e.target.value })}
            style={{ marginBottom: 16 }}
          />
          <TextField
            margin="dense"
            label="Bangla Name"
            type="text"
            fullWidth
            variant="outlined"
            value={addDialog.nameBn}
            onChange={(e) => setAddDialog({ ...addDialog, nameBn: e.target.value })}
            style={{ marginBottom: 16 }}
          />
          {addDialog.type === 'duaSubcat' && (
            <TextField
              select
              margin="dense"
              label="Parent Category"
              fullWidth
              variant="outlined"
              value={addDialog.categoryId}
              onChange={(e) => setAddDialog({ ...addDialog, categoryId: e.target.value })}
            >
              {duaCats.map(cat => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.nameEn || cat.name}
                </MenuItem>
              ))}
            </TextField>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAdd} color="primary">
            Cancel
          </Button>
          <Button onClick={saveAdd} color="primary" variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onClose={closeDelete} fullWidth maxWidth="xs">
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDelete} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmDelete} style={{ color: theme.palette.error.main }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} message={snackbar.message} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} />
    </AdminLayout>
  );
}
